import { Box, ButtonBase, Container, styled } from '@mui/material';
import { useState } from 'react';

import { routes } from '@/assets/constants/routes-no.ts';
import { CompetitionWinnersDialog } from '@/components/CompetitionWinnersDialog/index.ts';
import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWinner, CompetitionWithConfig } from '@/lib/api/requests.ts';
import { NumberCircle } from '@/ui/NumberCircle.tsx';
import { NumberedList, NumberedListItem } from '@/ui/NumberedList.tsx';

import { JANUARY_OPEN_2025_CAMPAIGN_END_DATE } from '../../../config.ts';
import Chicken from '../_assets/chicken.svg?styled';
import House from '../_assets/house.svg?styled';
import MountainsBG from '../_assets/mountains-bg.svg?styled';
import MountainsFG from '../_assets/mountains-fg.svg?styled';
import stars from '../_assets/stars.svg';
import Sun from '../_assets/sun.svg?styled';
import { CTA } from '../_components/CTA.tsx';
import { useHasEnded } from '../_components/useHasEnded.ts';

const dateFormatter = new Intl.DateTimeFormat('no', {
  day: 'numeric',
  month: 'long',
});

const dateStr = dateFormatter.format(JANUARY_OPEN_2025_CAMPAIGN_END_DATE);

const Root = styled('div')(({ theme }) => ({
  background:
    'linear-gradient(360deg, #FFF4E0 4.06%, #608FDA 31.63%, #1F4277 63.84%, #153668 80.46%, #072453 100%)',
  width: '100%',
  maxHeight: 'calc(100vh - var(--top-bar-height))',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  height: '780px',
  minHeight: '700px',

  [theme.breakpoints.up('md')]: {
    aspectRatio: '3 / 4',
    minHeight: '800px',
  },
  [theme.breakpoints.up('lg')]: {
    aspectRatio: '16 / 9',
    height: 'auto',
    minHeight: '900px',
  },
  [theme.breakpoints.up('xl')]: {
    maxHeight: '1000px',
  },
}));

export const TopSection = (props: {
  winners: CompetitionWinner[] | undefined;
  activeCompetition: CompetitionWithConfig | undefined;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ended = useHasEnded();

  return (
    <Root>
      <Background />
      <Container sx={{ color: 'white', mt: { xs: 6, md: 10 }, zIndex: '1' }} maxWidth="xl">
        <Box
          sx={{
            maxWidth: { xs: 370, md: 450, xl: 800 },
            mx: { xs: 'auto', sm: 0 },
            ml: { sm: 10 },
          }}
        >
          <h1 className="mb-2 typography-v1-h1 text-balance">
            Er det du som våkner gjeldfri {dateStr}?
          </h1>
          <p className="mb-8 typography-v1-h4">
            Én heldig vinner får slettet inntil 400 000 kr av sin eksisterende
            kreditt/forbruksgjeld.
          </p>
          <p className="mb-2 typography-v1-h4">For å delta:</p>

          <NumberedList
            sx={{ mb: 2 }}
            renderNumber={(props) => (
              <NumberCircle
                sx={{
                  fontSize: 14,
                  height: 20,
                  backgroundColor: '#FBFDFF',
                  color: '#031839',
                }}
                {...props}
              />
            )}
            renderLabel={(props) => <p {...props} className="typography-v1-body3" />}
          >
            <NumberedListItem>Logg inn med BankID/Vipps</NumberedListItem>
            <NumberedListItem>Sjekk om du kan få lavere rente på din gjeld</NumberedListItem>
            <NumberedListItem>Delta i trekningen</NumberedListItem>
          </NumberedList>

          {ended && (
            <p className="mb-6 typography-v1-h4">
              <span>Denne trekningen er avsluttet. </span>
              <ButtonBase
                onClick={() => setIsOpen(true)}
                sx={{
                  color: '#74C6FD',
                  textDecoration: 'underline',
                  mb: 0.5,
                  fontWeight: 700,
                  ml: 1,
                }}
              >
                Se vinneren her
              </ButtonBase>
            </p>
          )}

          <Box sx={{ mb: 2 }}>
            <a href={routes.JANUARY_OPEN_2025_COMPETITION_TERMS} style={{ color: 'inherit' }}>
              <span className="typography-v1-body3">Vilkår og betingelser for deltagelse</span>
            </a>
          </Box>

          <CTA
            activeCompetition={props.activeCompetition}
            onClick={() => {
              sendUserPortalEvent('jano25_competition_cta_top_clicked');
            }}
          />
        </Box>
      </Container>

      {props.winners && (
        <CompetitionWinnersDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          winners={props.winners}
        />
      )}
    </Root>
  );
};

const Background = () => (
  <>
    <Box
      sx={{
        position: 'absolute',
        top: 32,
        left: 22,
        right: 22,
        height: '150px',
        backgroundImage: `url("${stars}")`,
      }}
    />
    <Sun
      sx={{
        position: 'absolute',
        width: { xs: '85px', sm: '100px', lg: '180px', xl: '211px' },
        height: 'auto',
        bottom: { xs: 180, sm: 160, md: 150, lg: 160 },
        right: { xs: 0, lg: '5%' },
      }}
    />
    <MountainsBG
      sx={{
        position: 'absolute',
        left: { xs: '-40%', md: 0 },
        right: 0,
        bottom: { xs: 60, sm: 0, md: -20 },
        height: { xs: '220px', sm: '280px', md: '320px' },
        width: { xs: '150%', md: '120%', lg: '100%' },
      }}
      preserveAspectRatio="none"
    />
    <House
      sx={{
        position: 'absolute',
        bottom: { xs: 150, sm: 110, xl: 105 },
        left: { xs: -40, sm: '5%', md: '30%', lg: '20%' },
        width: { xs: '263px', sm: '300px', lg: '350px', xl: '500px' },
        height: 'auto',
      }}
    />
    <MountainsFG
      sx={{
        position: 'absolute',
        left: { xs: '-40%', md: 0 },
        right: 0,
        bottom: { xs: 60, sm: 0, md: -20 },
        height: { xs: '220px', sm: '280px', md: '320px' },
        width: { xs: '150%', md: '120%', lg: '100%' },
      }}
      preserveAspectRatio="none"
    />
    <Chicken
      sx={{
        position: 'absolute',
        bottom: { xs: 195, sm: 190, md: 165, lg: 205, xl: 220 },
        right: { xs: 35, sm: 20, md: 10, lg: '7%', xl: '6%' },
        width: { xs: '30px', sm: '35px', md: '45px', lg: '72px', xl: '80px' },
        height: 'auto',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: (theme) => theme.palette.background.contrast,
        py: 2,
      }}
    >
      <Container maxWidth="md">
        <div className="text-center typography-v1-body4 text-balance text-white">
          <LoanExampleText />
        </div>
      </Container>
    </Box>
  </>
);
